import {
  Action,
  combineReducers,
  configureStore,
  Reducer,
} from '@reduxjs/toolkit';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { publicApi, secureApi } from 'services/api';
import { appInitSlice } from 'services/app-init/appInitSlice';
import { localeSlice } from 'services/slices/locale';
import { resourcesHistorySlice } from 'services/slices/resourcesHistory';

export const appReducer = combineReducers({
  [publicApi.reducerPath]: publicApi.reducer,
  [secureApi.reducerPath]: secureApi.reducer,
  [appInitSlice.name]: appInitSlice.reducer,
  [localeSlice.name]: localeSlice.reducer,
  [resourcesHistorySlice.name]: resourcesHistorySlice.reducer,
});

export type AppState = ReturnType<typeof appReducer>;

const rootReducer: Reducer<AppState, Action> = (state, action) => {
  if (action.type === 'store/reset') {
    state = {
      // We must keep the API state to preserve internal subscription counters.
      // If reset, mounted components will stop receiving updates.
      [publicApi.reducerPath]: state?.[publicApi.reducerPath],
      [secureApi.reducerPath]: state?.[secureApi.reducerPath],
      // slices to keep, not included will be reset
      [localeSlice.name]: localeSlice.reducer,
      [resourcesHistorySlice.name]: resourcesHistorySlice.reducer,
    } as unknown as AppState;
  }

  return appReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware().concat(publicApi.middleware, secureApi.middleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppSelector = useSelector.withTypes<AppState>();
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppStore = useStore.withTypes<typeof store>();

export default store;
